<script setup lang="ts">
import Icon from "@/components/general/Icon/Icon.vue";
import { type VNode, useSlots } from "vue";

const props = defineProps<{
    id: string;
    label: string;
    helpText?: string;
    helpEventName?: string;
    errorMessage?: string;
    fieldNote?: string;
}>();

// Define the slots
defineSlots<{
    /** main questioncontent */
    default: () => VNode | string;
    /** help text content */
    help?: () => VNode | string;
}>();

const emit = defineEmits<{
    (e: "help-clicked", id: string): void;
}>();

const slots = useSlots();
const hasHelp = slots.help !== undefined || !!props.helpText;
</script>

<template>
    <fieldset class="question-fieldset">
        <legend class="question-legend">
            {{ label }}
            <div v-if="hasHelp">
                <button
                    :id="`help-${id}`"
                    type="button"
                    class="question-help-toggle"
                    data-bs-toggle="collapse"
                    :href="`#collapse-${id}`"
                    aria-expanded="false"
                    :aria-controls="`collapse-${id}`"
                    @click="emit('help-clicked', id)"
                >
                    <Icon name="question" type="png" class="question-icon" />
                </button>
            </div>
        </legend>

        <div
            v-if="hasHelp"
            :id="`collapse-${id}`"
            class="question-help-tip collapse"
        >
            <div class="question-help-body">
                <button
                    type="button"
                    class="question-help-close"
                    data-bs-toggle="collapse"
                    :href="`#collapse-${id}`"
                    aria-expanded="false"
                >
                    <Icon name="close" type="png" class="close-icon" />
                </button>
                <slot name="help">
                    <p>{{ helpText }}</p>
                </slot>
            </div>
        </div>

        <div class="question-slot">
            <slot />
        </div>

        <div v-if="errorMessage" class="question-error">
            <Icon name="warning" type="png" class="warning-icon" />
            <p>{{ errorMessage }}</p>
        </div>

        <div v-if="fieldNote" class="question-note">
            <p>{{ fieldNote }}</p>
        </div>
    </fieldset>
</template>

<style scoped>
.question-fieldset {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    border: none;
    padding: 0;
}

.question-legend {
    display: flex;
    align-items: baseline;
    font-size: var(--font-size-legend);
    font-weight: var(--font-weight-bold);
    margin-bottom: 0.5rem;
    position: relative;
}

.question-help-toggle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    margin-left: 5px;
    border: 2px solid var(--primary-pressed);
    border-radius: 50%;
    background-color: var(--pure-white);
    cursor: pointer;
    outline: none;
    transition: box-shadow 0.2s ease;
}

.question-help-toggle:hover,
.question-help-toggle:focus {
    box-shadow: 1px 1px 7px 1px var(--grey-warm);
}

.question-icon {
    width: 18px;
    height: 18px;
}

.question-help-body {
    position: relative;
    padding: 1rem 2rem 1rem 1rem;
    border: 1px solid var(--grey-light);
    background-color: var(--off-white);
    border-radius: 2px;
    margin-bottom: 20px;
}

.question-help-body p {
    color: var(--off-black);
    margin: 0;
}

.question-help-close {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    border: none;
    background: none;
    cursor: pointer;
    padding: 2px;
    line-height: 1;
}

.close-icon {
    width: 1rem;
    height: 1rem;
}

.question-error {
    display: flex;
    align-items: center;
    margin-top: 1rem;
    padding: 1rem 1.25rem;
    border-radius: 2px;
    background-color: var(--error-background);
    color: var(--error-text);
    border: 1px solid var(--error-border);
    gap: 0.5rem;
}

.question-error,
.question-error p {
    font-size: var(--font-size-input);
    font-weight: var(--font-weight-bold);
}

.warning-icon {
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
}

.question-error p {
    margin: 0;
    white-space: pre-line;
}
</style>
